import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { NativeDateAdapter } from '@angular/material/core';
import { OrgCountryEnum } from '@rezevent/api-types';
import { AUTH_COUNTRY_KEY } from './auth.service';

const MONTHS: Record<string, string[]> = {
  sq: ['Janar','Shkurt','Mars','Prill','Maj','Qershor','Korrik','Gusht','Shtator','Tetor','Nentor','Dhjetor'],
  de: ['Januar','Februar','März','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'],
};

const DAYS: Record<string, string[]> = {
  sq: ['Die','Hen','Mar','Mer','Enj','Pre','Sht'],
  de: ['So','Mo','Di','Mi','Do','Fr','Sa'],
};

@Injectable()
export class CustomDateAdapter extends NativeDateAdapter {
  private get lang() {
    return localStorage.getItem(AUTH_COUNTRY_KEY) === OrgCountryEnum.De ? 'de' : 'sq';
  }
  override format(
    date: Date,
    displayFormat: { year?: string; month?: string; day?: string }
  ): string {
    if (
      displayFormat.day === 'numeric' &&
      displayFormat.month === 'numeric' &&
      displayFormat.year === 'numeric'
    ) {
      const day = date.getDate();
      const month = date.getMonth() + 1;
      const year = date.getFullYear();

      return (
        day.toString().padStart(2, '0') +
        '.' +
        month.toString().padStart(2, '0') +
        '.' +
        year
      );
    }

    return super.format(date, displayFormat);
  }

  override parse(value: string): Date | null {
    if (value.length !== 8) return null;
    const day = Number(value.slice(0, 2));
    const month = Number(value.slice(2, 4));
    const year = Number(value.slice(4, 8));

    if (isNaN(day) || isNaN(month) || isNaN(year)) return null;

    return new Date(year, month, day);
  }

  override getDayOfWeekNames(): string[] {
    return DAYS[this.lang];
  }

  override getMonthNames(): string[] {
    return MONTHS[this.lang];
  }

  override getFirstDayOfWeek(): number {
    return 1;
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function groupBy<T, K extends keyof any>(
  array: T[],
  keyGetter: (item: T) => K
): Record<K, T[]> {
  return array.reduce(
    (result, item) => {
      const key = keyGetter(item);
      if (!result[key]) {
        result[key] = [];
      }
      result[key].push(item);
      return result;
    },
    {} as Record<K, T[]>
  );
}

export type ControlsInterface<T> = {
  [K in keyof T]-?: T[K] extends (infer U)[]
    ? FormArray<FormControl<U>>
    : T[K] extends object
      ? FormGroup<ControlsInterface<T[K]>>
      : FormControl<T[K]>;
};

export interface SelectOption {
  value: string | number;
  label: string;
}

export function createOptionsFromEnum(
  enumObj: Record<string, string | number>
): SelectOption[] {
  return Object.keys(enumObj).map(key => ({
    value: enumObj[key],
    label: key.charAt(0).toUpperCase() + key.slice(1).toLowerCase(),
  }));
}

export function objectToHttpParams(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  queryParams: Record<string, any>
): HttpParams {
  let httpParams = new HttpParams();
  for (const key of Object.keys(queryParams)) {
    const value = queryParams[key];
    if (value !== undefined && value !== null) {
      httpParams = httpParams.append(key, value);
    }
  }
  return httpParams;
}

export function markAllControlsAsTouched<T extends object>(
  controls: ControlsInterface<T>
) {
  for (const ctrlName in controls) {
    controls[ctrlName].markAsTouched();
  }
}

export function getEnumKeyFromValue(
  enumObj: Record<string, string>,
  value: string
): string | undefined {
  return Object.keys(enumObj).find(key => enumObj[key] === value);
}
